import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Bell, PackageCheck, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import type { OrderType, OrderStatus, NotificationStatus } from "@sbd-tcic/shared-types";
import { api, ApiClientError } from "../lib/api";
import { useToast } from "../lib/toast";
import { KpiTile } from "../components/KpiTile";
import { DataTable, type Column } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";

interface OrderRow {
  orderNumber: string;
  orderType: OrderType;
  orderMethod: string;
  serviceType: string;
  orderDate: string;
  customerId: string;
  customerName: string;
  value: number;
  status: OrderStatus;
  fulfillmentStatus: string;
  paymentMethod: string;
  notificationStatus: NotificationStatus;
  createdBy: string;
  reason: string;
}

interface SearchResult {
  results: OrderRow[];
  total: number;
  page: number;
  pageSize: number;
}

interface NotificationSummary {
  pendingApprovals: number;
  pendingPickUp: number;
  readyToShip: number;
}

const ORDER_TYPES: OrderType[] = ["Sales Order", "Repair Order", "Return Order", "Warranty Exchange", "Satisfaction Refund", "Quote"];
const PAGE_SIZE = 25;

const money = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function OrderHistory() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [summary, setSummary] = useState<NotificationSummary | null>(null);
  const [keyword, setKeyword] = useState("");
  const [orderType, setOrderType] = useState<OrderType | "">("");
  const [status, setStatus] = useState<OrderStatus | "">("");
  const [notificationStatus, setNotificationStatus] = useState<NotificationStatus | "">("");
  const [page, setPage] = useState(1);

  const [data, setData] = useState<SearchResult>({ results: [], total: 0, page: 1, pageSize: PAGE_SIZE });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void api
      .get<NotificationSummary>("/orders/notification-summary")
      .then(setSummary)
      .catch(() => setSummary(null));
  }, []);

  const runSearch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (keyword.trim()) params.set("keyword", keyword.trim());
      if (orderType) params.set("orderType", orderType);
      if (status) params.set("status", status);
      if (notificationStatus) params.set("notificationStatus", notificationStatus);
      params.set("page", String(page));
      params.set("pageSize", String(PAGE_SIZE));
      const result = await api.get<SearchResult>(`/orders?${params.toString()}`);
      setData(result);
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to load orders.", "error");
    } finally {
      setLoading(false);
    }
  }, [keyword, orderType, status, notificationStatus, page, toast]);

  useEffect(() => {
    const t = setTimeout(() => void runSearch(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, orderType, status, notificationStatus, page]);

  useEffect(() => {
    setPage(1);
  }, [keyword, orderType, status, notificationStatus]);

  const columns: Column<OrderRow>[] = [
    { key: "orderNumber", header: "Order #", render: (o) => <span className="font-bold text-brand-blue">{o.orderNumber}</span> },
    { key: "customerName", header: "Customer", render: (o) => o.customerName || "—" },
    { key: "orderType", header: "Type", render: (o) => o.orderType },
    { key: "orderDate", header: "Date", render: (o) => o.orderDate.slice(0, 10) },
    { key: "value", header: "Value", render: (o) => money(o.value) },
    { key: "status", header: "Status", render: (o) => <StatusBadge status={o.status} /> },
    { key: "fulfillmentStatus", header: "Fulfillment", render: (o) => <StatusBadge status={o.fulfillmentStatus} /> },
    { key: "notificationStatus", header: "Notification", render: (o) => <StatusBadge status={o.notificationStatus} /> },
  ];

  const totalPages = Math.max(1, Math.ceil(data.total / data.pageSize));

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
        <KpiTile icon={Bell} iconColor="orange" label="Pending Approvals" value={summary?.pendingApprovals ?? "—"} />
        <KpiTile icon={PackageCheck} iconColor="blue" label="Ready to Ship" value={summary?.readyToShip ?? "—"} />
        <KpiTile icon={Clock} iconColor="purple" label="Pending Pick-Up" value={summary?.pendingPickUp ?? "—"} />
      </div>

      <div className="card p-5">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-3.5">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input
              className="input pl-8"
              placeholder="Search by order # or customer name…"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
          </div>
          <select className="input lg:w-44" value={orderType} onChange={(e) => setOrderType(e.target.value as OrderType | "")}>
            <option value="">All Order Types</option>
            {ORDER_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <select className="input lg:w-32" value={status} onChange={(e) => setStatus(e.target.value as OrderStatus | "")}>
            <option value="">All Statuses</option>
            <option value="Open">Open</option>
            <option value="Closed">Closed</option>
          </select>
          <select
            className="input lg:w-44"
            value={notificationStatus}
            onChange={(e) => setNotificationStatus(e.target.value as NotificationStatus | "")}
          >
            <option value="">All Notifications</option>
            <option value="Sent">Sent</option>
            <option value="Not Sent">Not Sent</option>
            <option value="Pending Approval">Pending Approval</option>
            <option value="Backorder Parts">Backorder Parts</option>
            <option value="Shipped">Shipped</option>
          </select>
        </div>

        <DataTable
          columns={columns}
          rows={data.results}
          rowKey={(o) => o.orderNumber}
          onRowClick={(o) => navigate(`/orders/${o.orderNumber}`)}
          emptyMessage={loading ? "Loading orders…" : "No orders match this search."}
        />

        <div className="flex items-center justify-between mt-3.5 text-xs text-ink-muted">
          <span>
            {data.total} order{data.total === 1 ? "" : "s"} · page {data.page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button className="btn-outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <ChevronLeft size={14} /> Prev
            </button>
            <button className="btn-outline" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
